import { useMemo, useRef, useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import {
  Bell,
  ChevronRight,
  CircleCheck,
  Clock,
  CreditCard,
  LogOut,
  UserPlus,
  Utensils,
  type LucideIcon,
} from "lucide-react"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { FilterChip } from "@/components/ui/filter-chip"
import { PageHeader } from "@/components/ui/headers"
import { OccupancyBar, OccupancyLegendItem, occupancyPercent } from "@/features/staff/occupancy"
import {
  ALERT_REASONS_BY_PRIORITY,
  alertMessageFor,
  staffMutationResourceKey,
  type AlertReason,
} from "@/features/staff/table-status"
import { intlLocale, t } from "@/lib/i18n"
import {
  postInline,
  putInline,
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
  type InlineUpdateResult,
} from "@/lib/inline-update"
import { cn } from "@/lib/utils"

type TablesSummary = {
  total: number
  available: number
  occupied: number
  reserved: number
  disabled: number
}

/** One table asking for a person, straight from Staff::FloorAttentionQuery. */
type AttentionRow = {
  table_external_id: string
  table_name: string
  reason: AlertReason
  since: string
}

type PipelineStage = {
  key: string
  count: number
  /** When the oldest order in this stage entered it. Null on an empty stage. */
  oldest_at: string | null
}

type Pipeline = {
  stages: PipelineStage[]
  total: number
}

type RevenueSummary = {
  /** Bill totals plus tips collected today; see `today_tips` for the tip share. */
  today_total: number
  today_tips: number
  today_count: number
  pending_total: number
  pending_count: number
  pending_oldest_at: string | null
  currency: string
}

type DashboardData = {
  tables: TablesSummary
  pipeline: Pipeline
  attention: AttentionRow[]
  revenue: RevenueSummary
}

const I18N_BASE = "ui.react.pages.staff.location.dashboards.show"

type GroupKey = "all" | "door" | "service" | "payment" | "tables"

/**
 * Which filter chip a reason answers to. Grouping is a property of this screen
 * rather than of the domain, so it lives here and not in the payload: the server
 * sends the reason, and the five reasons are the contract.
 */
const REASON_GROUP: Record<AlertReason, Exclude<GroupKey, "all">> = {
  entry_requested: "door",
  waiter_requested: "service",
  ready_unserved: "service",
  payment_pending: "payment",
  awaiting_release: "tables",
}

const REASON_ICON: Record<AlertReason, LucideIcon> = {
  entry_requested: UserPlus,
  waiter_requested: Bell,
  ready_unserved: Utensils,
  payment_pending: CreditCard,
  awaiting_release: LogOut,
}

/**
 * What the row's button does.
 *
 * Only two reasons have a one-tap answer that already exists on the server, and
 * both are idempotent-by-design: acknowledging a quiet table is a no-op, and
 * `release` is refused unless the table may be released. The other three end in
 * a decision that needs the table in front of you — which diner to let in, which
 * plate went cold, which charge to confirm — so they navigate rather than
 * pretending a dashboard can settle them.
 */
const REASON_ACTION: Record<AlertReason, "acknowledge" | "release" | "open"> = {
  entry_requested: "open",
  waiter_requested: "acknowledge",
  ready_unserved: "open",
  payment_pending: "open",
  awaiting_release: "release",
}

const ACTION_LABEL_KEY: Record<"acknowledge" | "release" | "open", string> = {
  acknowledge: "action_acknowledge",
  release: "action_release",
  open: "action_open_table",
}

const GROUP_LABEL_KEY: Record<GroupKey, string> = {
  all: "group_all",
  door: "group_door",
  service: "group_service",
  payment: "group_payment",
  tables: "group_tables",
}

const GROUP_ORDER: GroupKey[] = ["all", "door", "service", "payment", "tables"]

/**
 * Age drives the colour; the reason drives the icon.
 *
 * A waiter call one minute old is not urgent and the same call at twelve minutes
 * is, so tone cannot come from the reason alone. The thresholds mirror the two
 * the server already reasons in — five minutes is when a payment goes stale, ten
 * is when a ready plate does — rather than inventing a third scale.
 */
function ageTone(minutes: number): "calm" | "warn" | "urgent" {
  if (minutes >= 10) return "urgent"
  if (minutes >= 5) return "warn"
  return "calm"
}

const TONE_TEXT: Record<"calm" | "warn" | "urgent", string> = {
  calm: "text-muted-foreground",
  warn: "text-status-occupied",
  urgent: "text-destructive font-semibold",
}

const TONE_TILE: Record<"calm" | "warn" | "urgent", string> = {
  calm: "bg-muted text-muted-foreground",
  warn: "bg-status-occupied/10 text-status-occupied",
  urgent: "bg-destructive/10 text-destructive",
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat(intlLocale(), {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

function minutesSince(iso: string) {
  return Math.max(Math.round((Date.now() - new Date(iso).getTime()) / 60000), 0)
}

function formatRelativeTime(iso: string) {
  const date = new Date(iso)
  const diffSeconds = Math.max(Math.round((Date.now() - date.getTime()) / 1000), 1)
  const rtf = new Intl.RelativeTimeFormat(intlLocale(), { numeric: "auto", style: "narrow" })

  if (diffSeconds < 60) return rtf.format(-diffSeconds, "second")
  if (diffSeconds < 3600) return rtf.format(-Math.round(diffSeconds / 60), "minute")
  if (diffSeconds < 86400) return rtf.format(-Math.round(diffSeconds / 3600), "hour")

  return date.toLocaleString(intlLocale(), {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function AttentionQueue({ rows }: { rows: AttentionRow[] }) {
  const [group, setGroup] = useState<GroupKey>("all")
  const [processing, setProcessing] = useState<Set<string>>(new Set())
  const locksRef = useRef<Set<string>>(new Set())

  // The server already sends these ordered, but the front re-sorts rather than
  // trusting arrival order — same reasoning as tables-overview, so a payload
  // change can never silently downgrade what staff reads first.
  const ordered = useMemo(
    () =>
      [...rows].sort((left, right) => {
        const byReason =
          ALERT_REASONS_BY_PRIORITY.indexOf(left.reason) -
          ALERT_REASONS_BY_PRIORITY.indexOf(right.reason)
        if (byReason !== 0) return byReason

        return new Date(left.since).getTime() - new Date(right.since).getTime()
      }),
    [rows]
  )

  const counts = useMemo(() => {
    const tally: Record<GroupKey, number> = { all: ordered.length, door: 0, service: 0, payment: 0, tables: 0 }
    ordered.forEach((row) => {
      tally[REASON_GROUP[row.reason]] += 1
    })
    return tally
  }, [ordered])

  const visible = group === "all" ? ordered : ordered.filter((row) => REASON_GROUP[row.reason] === group)

  // One lock per table, so a second tap while the first request is in flight is
  // dropped rather than racing it (mirrors runTableMutation in tables-overview).
  const runMutation = (row: AttentionRow, performRequest: () => Promise<InlineUpdateResult>) => {
    const resourceKey = staffMutationResourceKey("table", row.table_external_id)
    if (locksRef.current.has(resourceKey)) return

    locksRef.current.add(resourceKey)
    setProcessing((current) => new Set(current).add(resourceKey))
    performRequest()
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => {
        locksRef.current.delete(resourceKey)
        setProcessing((current) => {
          const next = new Set(current)
          next.delete(resourceKey)
          return next
        })
      })
  }

  const handleAction = (row: AttentionRow) => {
    const action = REASON_ACTION[row.reason]

    if (action === "open") {
      router.visit(`/tables/${row.table_external_id}`)
      return
    }

    if (action === "acknowledge") {
      runMutation(row, () =>
        postInline(`/tables/${row.table_external_id}/acknowledge_waiter_call`, {}, { preserveState: true })
      )
      return
    }

    runMutation(row, () =>
      putInline(`/tables/${row.table_external_id}/event`, { event: "release" }, { preserveState: true })
    )
  }

  return (
    <Card density="compact" data-testid="dashboard-attention">
      <CardHeader>
        <CardTitle>{t(`${I18N_BASE}.attention_title`)}</CardTitle>
        <CardDescription>{t(`${I18N_BASE}.attention_subtitle`)}</CardDescription>
        <CardAction>
          <span
            className={cn(
              "inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-sm font-semibold tabular-nums",
              ordered.length === 0
                ? "bg-muted text-muted-foreground"
                : "bg-destructive/10 text-destructive"
            )}
            data-testid="dashboard-attention-count"
          >
            {ordered.length}
          </span>
        </CardAction>
      </CardHeader>

      <CardContent className="pb-0">
        <div className="flex flex-wrap gap-1.5">
          {GROUP_ORDER.map((key) => (
            <FilterChip
              key={key}
              active={group === key}
              count={counts[key]}
              onClick={() => setGroup(key)}
              data-testid={`dashboard-attention-filter-${key}`}
            >
              {t(`${I18N_BASE}.${GROUP_LABEL_KEY[key]}`)}
            </FilterChip>
          ))}
        </div>
      </CardContent>

      {visible.length === 0 ? (
        <div className="mt-3 flex flex-col items-center gap-2 border-t px-4 py-10 text-center">
          <CircleCheck className="size-5 text-status-available" />
          <p className="text-sm font-medium">
            {ordered.length === 0
              ? t(`${I18N_BASE}.attention_empty_title`)
              : t(`${I18N_BASE}.attention_filtered_title`, {
                  group: t(`${I18N_BASE}.${GROUP_LABEL_KEY[group]}`),
                })}
          </p>
          <p className="max-w-xs text-xs text-muted-foreground">
            {ordered.length === 0
              ? t(`${I18N_BASE}.attention_empty_hint`)
              : t(`${I18N_BASE}.attention_filtered_hint`, { count: ordered.length })}
          </p>
        </div>
      ) : (
        <ul className="mt-3 divide-y border-t">
          {visible.map((row) => {
            const minutes = minutesSince(row.since)
            const tone = ageTone(minutes)
            const Icon = REASON_ICON[row.reason]
            const action = REASON_ACTION[row.reason]
            const busy = processing.has(staffMutationResourceKey("table", row.table_external_id))

            return (
              <li
                key={`${row.table_external_id}-${row.reason}`}
                className="flex items-center gap-3 px-4 py-3"
                data-testid={`dashboard-attention-row-${row.reason}`}
              >
                <span
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-md",
                    TONE_TILE[tone]
                  )}
                >
                  <Icon className="size-4" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="shrink-0 text-sm font-semibold">{row.table_name}</span>
                    <span
                      className={cn(
                        "truncate text-sm",
                        tone === "urgent" ? "font-medium text-foreground" : "text-muted-foreground"
                      )}
                    >
                      {alertMessageFor([row.reason])}
                    </span>
                  </div>
                </div>

                <span
                  className={cn(
                    "inline-flex shrink-0 items-center gap-1 text-xs tabular-nums",
                    TONE_TEXT[tone]
                  )}
                >
                  <Clock className="size-3" />
                  {formatRelativeTime(row.since)}
                </span>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={busy}
                  onClick={() => handleAction(row)}
                  data-testid={`dashboard-attention-action-${row.table_external_id}`}
                >
                  {t(`${I18N_BASE}.${ACTION_LABEL_KEY[action]}`)}
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

const STAGE_LABEL_KEY: Record<string, string> = {
  pending: "stage_pending",
  in_progress: "stage_in_progress",
  ready: "stage_ready",
}

/** Only `ready` escalates: a plate nobody carried is the stage that goes cold. */
const STAGE_DOT: Record<string, string> = {
  pending: "border border-muted-foreground/50 bg-transparent",
  in_progress: "bg-status-occupied",
  ready: "bg-status-occupied",
}

function KitchenPipeline({ pipeline }: { pipeline: Pipeline }) {
  return (
    <Card density="compact" className="lg:col-span-2" data-testid="dashboard-pipeline">
      <CardHeader>
        <CardTitle>{t(`${I18N_BASE}.kitchen`)}</CardTitle>
        <CardAction>
          <Link
            href="/orders"
            className="inline-flex items-center gap-0.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            data-testid="dashboard-view-orders-link"
          >
            {t(`${I18N_BASE}.view_orders`)}
            <ChevronRight className="size-4" />
          </Link>
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="flex items-stretch gap-2">
          {pipeline.stages.map((stage) => {
            const minutes = stage.oldest_at ? minutesSince(stage.oldest_at) : 0
            const stale = stage.key === "ready" && stage.oldest_at !== null && minutes >= 10

            return (
              <Link
                key={stage.key}
                href={`/orders?status=${stage.key}`}
                className="flex-1 rounded-lg border p-3 transition-colors hover:border-ring/60"
                data-testid={`dashboard-pipeline-stage-${stage.key}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={cn(
                      "text-2xl font-semibold tabular-nums tracking-tight",
                      stage.count === 0 && "text-muted-foreground/40"
                    )}
                  >
                    {stage.count}
                  </span>
                  {stage.count > 0 && (
                    <span
                      className={cn(
                        "size-2 shrink-0 rounded-full",
                        stale ? "bg-destructive" : STAGE_DOT[stage.key]
                      )}
                    />
                  )}
                </div>
                <p className="mt-1.5 text-sm font-medium">
                  {t(`${I18N_BASE}.${STAGE_LABEL_KEY[stage.key]}`)}
                </p>
                <p className={cn("text-xs", stale ? "text-destructive" : "text-muted-foreground")}>
                  {stage.oldest_at
                    ? t(`${I18N_BASE}.stage_oldest`, { age: formatRelativeTime(stage.oldest_at) })
                    : t(`${I18N_BASE}.stage_empty`)}
                </p>
              </Link>
            )
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          {t(`${I18N_BASE}.pipeline_hint`, { minutes: 10 })}
        </p>
      </CardContent>
    </Card>
  )
}

function DiningRoom({ tables }: { tables: TablesSummary }) {
  const percent = occupancyPercent(tables)

  return (
    <Card density="compact" data-testid="dashboard-dining-room">
      <CardHeader>
        <CardTitle>{t(`${I18N_BASE}.dining_room`)}</CardTitle>
        {tables.total > 0 && (
          <CardAction className="text-sm font-medium tabular-nums">
            {t(`${I18N_BASE}.occupancy`, { percent })}
          </CardAction>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        <OccupancyBar summary={tables} />
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          <OccupancyLegendItem
            count={tables.occupied}
            label={t(`${I18N_BASE}.status_occupied`)}
            dotClassName="bg-status-occupied"
          />
          <OccupancyLegendItem
            count={tables.reserved}
            label={t(`${I18N_BASE}.status_reserved`)}
            dotClassName="bg-status-reserved"
          />
          <OccupancyLegendItem
            count={tables.available}
            label={t(`${I18N_BASE}.status_available`)}
            dotClassName="bg-status-available"
          />
          <OccupancyLegendItem
            count={tables.disabled}
            label={t(`${I18N_BASE}.status_disabled`)}
            dotClassName="bg-status-disabled/40"
          />
        </div>
        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <span className="text-xs text-muted-foreground">
            {tables.total === 0
              ? t(`${I18N_BASE}.no_tables`)
              : t(`${I18N_BASE}.tables_count`, { count: tables.total })}
          </span>
          <Link
            href="/tables"
            className="inline-flex items-center gap-0.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            data-testid="dashboard-view-tables-link"
          >
            {t(`${I18N_BASE}.view_tables`)}
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * The money, last and quietest. The page descends by urgency, and a figure
 * nobody has to act on this minute belongs under the ones they do.
 */
function TillBand({ revenue }: { revenue: RevenueSummary }) {
  return (
    <Card density="compact" data-testid="dashboard-till">
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{t(`${I18N_BASE}.todays_till`)}</p>
          <p className="text-xl font-semibold tabular-nums tracking-tight">
            {formatCurrency(revenue.today_total, revenue.currency)}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(`${I18N_BASE}.collected_today`, { count: revenue.today_count })}
            {revenue.today_tips > 0 && (
              <>
                {" · "}
                {t(`${I18N_BASE}.tips_today`, {
                  amount: formatCurrency(revenue.today_tips, revenue.currency),
                })}
              </>
            )}
          </p>
        </div>

        <div className="space-y-1 sm:border-l sm:pl-4">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {revenue.pending_count > 0 && (
                <span className="size-1.5 rounded-full bg-status-occupied" />
              )}
              {t(`${I18N_BASE}.to_collect`)}
            </p>
            <Link
              href="/bills"
              className="inline-flex items-center gap-0.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              data-testid="dashboard-view-bills-link"
            >
              {t(`${I18N_BASE}.view_bills`)}
              <ChevronRight className="size-4" />
            </Link>
          </div>
          <p className="text-xl font-semibold tabular-nums tracking-tight">
            {formatCurrency(revenue.pending_total, revenue.currency)}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(`${I18N_BASE}.open_bills`, { count: revenue.pending_count })}
            {revenue.pending_oldest_at && (
              <>
                {" · "}
                {t(`${I18N_BASE}.oldest_open_bill`, {
                  age: formatRelativeTime(revenue.pending_oldest_at),
                })}
              </>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export default function StaffLocationDashboardsShow() {
  const { dashboard } = usePage<{ dashboard: DashboardData }>().props

  const { tables, pipeline, attention, revenue } = dashboard

  return (
    <StaffLayout>
      <MainContainer size="md">
        <div className="space-y-4 pb-6" data-testid="location-dashboard-card">
          {/* Not the location name: the switcher in the shell header already
              names it, on this screen and on every other one. A page title that
              repeats the thing directly above it says nothing about the page —
              and this one is the floor's live queue, so it says so. */}
          <PageHeader title={t(`${I18N_BASE}.title`)} description={t(`${I18N_BASE}.overview`)} />

          <AttentionQueue rows={attention} />

          <div className="grid items-start gap-4 lg:grid-cols-3">
            <KitchenPipeline pipeline={pipeline} />
            <DiningRoom tables={tables} />
          </div>

          <TillBand revenue={revenue} />
        </div>
      </MainContainer>
    </StaffLayout>
  )
}
